import * as React from 'react';
import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { LS_THEME_PREFERENCE } from 'constants/local-storage';

import {
    applyTheme,
    getSystemTheme,
    getThemeColors,
    getThemePreference,
    parseThemePreference,
    saveThemePreference,
    SYSTEM_THEME_QUERY,
    ThemePreference,
} from 'helpers/theme';

import { ThemeContext } from 'hooks/useTheme';

const ThemeProvider = ({ children }: { children: ReactNode }) => {
    const [preference, setStoredPreference] = useState(getThemePreference);
    const [systemTheme, setSystemTheme] = useState(getSystemTheme);
    const theme = preference === 'system' ? systemTheme : preference;

    useLayoutEffect(() => applyTheme(theme), [theme]);

    useEffect(() => {
        const media = window.matchMedia?.(SYSTEM_THEME_QUERY);
        const onSystemChange = () => setSystemTheme(getSystemTheme());
        const onStorageChange = (event: StorageEvent) => {
            if (event.key === LS_THEME_PREFERENCE || event.key === null) {
                setStoredPreference(parseThemePreference(event.newValue));
            }
        };
        onSystemChange();
        media?.addEventListener('change', onSystemChange);
        window.addEventListener('storage', onStorageChange);
        return () => {
            media?.removeEventListener('change', onSystemChange);
            window.removeEventListener('storage', onStorageChange);
        };
    }, []);

    const setPreference = useCallback((next: ThemePreference) => {
        saveThemePreference(next);
        setStoredPreference(next);
    }, []);

    const value = useMemo(
        () => ({ preference, theme, colors: getThemeColors(theme), setPreference }),
        [preference, theme, setPreference],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeProvider;
